"use client";

import type { Attachment } from 'ai';
import { useEffect, useState } from 'react';

import { LoaderIcon } from './icons';
import { Button } from './ui/button';
import {
  X,
  FileText,
  FileSpreadsheet,
  FileJson,
  File as FileIcon,
  ExternalLink,
} from 'lucide-react';

export const PreviewAttachment = ({
  attachment,
  isUploading = false,
  onRemove,
}: {
  attachment: Attachment;
  isUploading?: boolean;
  onRemove?: (attachment: Attachment) => void;
}) => {
  const { name, url, contentType } = attachment;

  const lower = (name || '').toLowerCase();
  const isImage = contentType?.startsWith('image');
  const isCSV = lower.endsWith('.csv');
  const isJSON = lower.endsWith('.json');
  const isExcel = lower.endsWith('.xlsx') || lower.endsWith('.xls');
  const isPDF = lower.endsWith('.pdf');
  const isTXT = lower.endsWith('.txt');
  const isDOCX = lower.endsWith('.docx');

  const fileKind: 'image' | 'sheet' | 'json' | 'doc' | 'other' = isImage
    ? 'image'
    : isCSV || isExcel
    ? 'sheet'
    : isJSON
    ? 'json'
    : isPDF || isTXT || isDOCX
    ? 'doc'
    : 'other';

  const ExtBadge = () => (
    <span className="absolute bottom-1 left-1 rounded px-1.5 py-0.5 text-[10px] font-medium bg-background/70 text-muted-foreground border border-border backdrop-blur">
      {(lower.split('.').pop() || '').toUpperCase()}
    </span>
  );

  // Small, safe previews (client-side only)
  const [csvPreview, setCsvPreview] = useState<string[][] | null>(null);
  const [jsonPreview, setJsonPreview] = useState<string | null>(null);
  const [txtPreview, setTxtPreview] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchText = async () => {
      if (!url) return;
      try {
        if (isCSV) {
          const text = await fetch(url).then((r) => r.text());
          if (cancelled) return;
          const head = text.slice(0, 4000);
          const lines = head.split(/\r?\n/).filter(Boolean).slice(0, 3);
          const rows = lines.map((line) =>
            line
              .split(/,(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)/) // naive CSV split respecting quotes
              .map((cell) => cell.replace(/^\"|\"$/g, ''))
              .slice(0, 4)
          );
          setCsvPreview(rows);
        } else if (isJSON) {
          const text = await fetch(url).then((r) => r.text());
          if (cancelled) return;
          let pretty = '';
          try {
            const obj = JSON.parse(text);
            const keys = Object.keys(obj).slice(0, 4);
            const mini: Record<string, unknown> = {};
            keys.forEach((k) => (mini[k] = obj[k]));
            pretty = JSON.stringify(mini, null, 2).slice(0, 300);
          } catch {
            pretty = text.slice(0, 300);
          }
          setJsonPreview(pretty);
        } else if (isTXT) {
          const text = await fetch(url).then((r) => r.text());
          if (cancelled) return;
          setTxtPreview(text.slice(0, 300));
        }
      } catch {
        // silent fail -> fallback to icon-only
      }
    };
    fetchText();
    return () => {
      cancelled = true;
    };
  }, [url, isCSV, isJSON, isTXT]);

  const NonImagePreview = () => {
    const Icon =
      fileKind === 'sheet'
        ? FileSpreadsheet
        : fileKind === 'json'
        ? FileJson
        : fileKind === 'doc'
        ? FileText
        : FileIcon;
    const hasCSV = isCSV && csvPreview && csvPreview.length > 0;
    const hasJSON = isJSON && !!jsonPreview;
    const hasTXT = isTXT && !!txtPreview;

    if (hasCSV) {
      const cols = Math.min(4, Math.max(...csvPreview.map((r) => r.length)) || 1);
      return (
        <div className="size-full p-1">
          <div
            className="grid gap-px h-full"
            style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
          >
            {csvPreview.map((row, ri) =>
              Array.from({ length: cols }).map((_, ci) => (
                <div
                  key={`${ri}-${ci}`}
                  className="bg-muted/40 border border-border/60 rounded-sm px-1 py-0.5 overflow-hidden"
                >
                  <div className="text-[10px] leading-tight text-muted-foreground truncate">
                    {row[ci] ?? ''}
                  </div>
                </div>
              ))
            )}
          </div>
          <ExtBadge />
        </div>
      );
    }

    if (hasJSON || hasTXT) {
      const text = hasJSON ? jsonPreview! : txtPreview!;
      return (
        <div className="size-full p-1">
          <pre className="text-[10px] leading-tight text-muted-foreground bg-muted/30 rounded-sm h-full w-full p-1 overflow-hidden whitespace-pre-wrap">
            {text}
          </pre>
          <ExtBadge />
        </div>
      );
    }

    if (isExcel) {
      // Elegant grid placeholder for sheets
      return (
        <div className="size-full p-2">
          <div className="grid grid-cols-4 gap-[3px] h-full">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="bg-muted/40 border border-border/60 rounded-sm"
              />
            ))}
          </div>
          <ExtBadge />
        </div>
      );
    }

    // Generic icon fallback
    return (
      <div className="size-full flex items-center justify-center">
        <Icon className="h-6 w-6 text-muted-foreground" />
        <ExtBadge />
      </div>
    );
  };

  return (
    <div data-testid="input-attachment-preview" className="flex flex-col gap-2">
      <div className="w-20 h-16 aspect-video bg-card rounded-md relative flex flex-col items-center justify-center group border border-border overflow-hidden">
        {isImage && url ? (
          // NOTE: it is recommended to use next/image for images
          // eslint-disable-next-line @next/next/no-img-element
          <img
            key={url}
            src={url}
            alt={name ?? 'image attachment'}
            className="rounded-md size-full object-cover"
          />
        ) : (
          <NonImagePreview />
        )}

        {isUploading && (
          <div
            data-testid="input-attachment-loader"
            className="animate-spin absolute text-muted-foreground"
          >
            <LoaderIcon />
          </div>
        )}

        {/* Quick open overlay for docs */}
        {!isImage && url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-1 right-1 inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] bg-background/70 text-muted-foreground border border-border hover:bg-muted backdrop-blur"
            aria-label="Open attachment"
          >
            <ExternalLink className="h-3 w-3" />
          </a>
        )}

        {onRemove && (
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className="absolute top-1 right-1 h-4 w-4 rounded-full bg-background hover:bg-muted shadow-sm border border-border"
            onClick={() => onRemove(attachment)}
          >
            <X className="h-2.5 w-2.5 text-muted-foreground" />
          </Button>
        )}
      </div>
      <div className="text-xs text-muted-foreground max-w-20 truncate">{name}</div>
    </div>
  );
};
